import {
  ButtonGroup,
  GhostButton,
  PrimaryButton,
  Stack,
  Text,
  Space,
  showToast,
} from "@certn/cascadia";
import { Divider, SectionMessage } from "@certn/cascadia-react";
import { CascadiaControlProps, CascadiaJsonFormContext } from "@certn/forms";
import { useContext, useMemo } from "react";

import { Trans, useTranslation } from "react-i18next";
import { css } from "@emotion/css";
import { useApplication } from "@certn/application";

import { Formik, FormikErrors, FormikProps } from "formik";
import { CustomQuestionnaireField } from "./CustomQuestionnaireField";
import {
  buildCustomQuestionnaireFormSchema,
  isConditionFulfilled,
} from "./utils";
import isEqual from "lodash/isEqual";
import merge from "lodash/merge";
import * as Yup from "yup";
import {
  CustomQuestionnaireAnswerType,
  CustomQuestionnaireFormType,
  CUSTOM_QUESTIONNAIRE_KEY,
} from "./types";
import { CustomQuestionnaireLayoutContext } from "../../layouts/CustomQuestionnaireLayoutContext";
import isNil from "lodash/isNil";

export function CustomQuestionnaireControl(props: CascadiaControlProps) {
  const { path } = props;
  const { t } = useTranslation();

  const {
    sectionLabel,
    subLabel,
    templateData,
    apiError: error,
  } = useContext(CustomQuestionnaireLayoutContext);

  const {
    submit,
    setValues: setContextValues,
    values: applicantFormData,
  } = useContext(CascadiaJsonFormContext);

  const { prevSection, navigateToSection } = useApplication();

  const templateIds = JSON.stringify(templateData?.templates.map((t) => t.id));

  const initialValues: CustomQuestionnaireFormType[] = useMemo(
    () =>
      (templateData?.templates ?? []).map((template, index) => {
        const isCurrent = isEqual(index, 0);
        const answers: CustomQuestionnaireAnswerType =
          Object.fromEntries(
            template.template_input_claims.map((claim) => {
              // Prefill the form with any existing data from the application context
              const answer =
                applicantFormData[CUSTOM_QUESTIONNAIRE_KEY]?.[claim.name] ??
                undefined;

              return [claim.name, answer];
            })
          ) ?? {};

        return {
          templateId: template.id,
          name: template.name,
          isCurrent,
          claims: [...template.template_input_claims],
          conditions: [...template.template_input_claim_conditions],
          answers,
        };
      }),
    // Only recompute when the set of templates changes, not on every field edit
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [templateIds]
  );

  if (error) {
    return (
      <SectionMessage
        type="danger"
        sectionTitle={t(
          "An error occurred while loading custom questionnaire templates. Please try again later."
        )}
        icon="error"
      >
        {error.message}
      </SectionMessage>
    );
  }

  // Impossible state: they cannot order a custom questionnaire check without providing at least 1 template
  if (!templateData || templateData.templates.length === 0) {
    return (
      <Stack>
        <SectionMessage
          icon="warning"
          sectionTitle={t("No questionnaire templates available.")}
          type="danger"
        />
      </Stack>
    );
  }

  const hasCompletedAllRequiredCustomQuestions = (
    isValid: boolean,
    values: CustomQuestionnaireFormType[]
  ): boolean => {
    const hasEmptyValues = values.some((v) =>
      Object.values(v.answers).some((value) => value === "")
    );

    return isValid && !hasEmptyValues;
  };

  const updateValues = (input: {
    currentTemplateId: string;
    claim: string;
    answer: string | number | boolean | null;
    setValues: FormikProps<CustomQuestionnaireFormType[]>["setValues"];
  }) => {
    const { currentTemplateId, claim, answer, setValues } = input;

    setValues((prev) =>
      prev.map((p) =>
        p.templateId === currentTemplateId
          ? {
              ...p,
              answers: {
                ...p.answers,
                // Yup treats null as an error, so we're using undefined here
                [claim]: answer === "" ? undefined : answer,
              },
            }
          : p
      )
    );
  };

  const updateContextValues = (input: {
    claim: string;
    answer: string | number | boolean | null;
    isValid: boolean;
    values: CustomQuestionnaireFormType[];
  }) => {
    const { claim, answer, isValid, values } = input;

    const hasCompletedRequiredCustomQuestions =
      hasCompletedAllRequiredCustomQuestions(isValid, values);

    const answers = {
      ...applicantFormData[CUSTOM_QUESTIONNAIRE_KEY],
      [claim]: answer === "" ? null : answer,
    };

    const templates = templateData?.templates || [];
    const conditions = templates
      .filter((template) =>
        template.template_input_claim_conditions.some(
          (c) => c.parent_claim_key === claim
        )
      )
      .flatMap((template) => template.template_input_claim_conditions);

    const conditionalClaims = conditions.filter(
      (conditionalClaim) =>
        !isConditionFulfilled({
          condition: conditionalClaim,
          answers,
        })
    );

    // If a condition is unfulfilled, set the conditional claim to null
    const conditionalAnswers = conditionalClaims.reduce(
      (acc: CustomQuestionnaireAnswerType, conditionalClaim) => {
        acc[conditionalClaim.dependent_claim_key] = null;
        return acc;
      },
      {}
    );

    setContextValues({
      ...applicantFormData,
      [CUSTOM_QUESTIONNAIRE_KEY]: {
        ...answers,
        ...conditionalAnswers,
      },
      [path]: hasCompletedRequiredCustomQuestions,
    });
  };

  const hasAnswerError = (
    errors: (FormikErrors<CustomQuestionnaireFormType> | undefined)[],
    answers: CustomQuestionnaireAnswerType
  ): boolean => {
    const answerKeys = Object.keys(answers);

    return Object.values(errors).some((errorGroup) =>
      Object.keys(errorGroup?.answers ?? {}).some((errorKey) =>
        answerKeys.includes(errorKey)
      )
    );
  };

  const handleNext = async (
    values: CustomQuestionnaireFormType[],
    setValues: FormikProps<CustomQuestionnaireFormType[]>["setValues"],
    isValid: boolean,
    errors: (FormikErrors<CustomQuestionnaireFormType> | undefined)[]
  ) => {
    const currentTemplate = values.find((value) => !!value.isCurrent);

    const hasErrors = hasAnswerError(errors, currentTemplate?.answers || {});

    if (hasErrors) {
      showToast(t("One or more required fields are missing."), {
        type: "error",
      });
      return;
    }

    const templates = templateData?.templates || [];

    const currentIndex = templates.findIndex(
      (t) => t.id === currentTemplate?.templateId
    );

    const isLastTemplate = currentIndex === templates.length - 1;

    const answers = merge({}, ...values.map((value) => value.answers));

    const hasCompletedRequiredCustomQuestions =
      hasCompletedAllRequiredCustomQuestions(isValid, values);

    // Always preserve state in context in case the user wants to save & exit without filling all the template questions
    setContextValues({
      ...applicantFormData,
      [CUSTOM_QUESTIONNAIRE_KEY]: answers,
      [path]: hasCompletedRequiredCustomQuestions,
    });

    if (isLastTemplate) {
      submit();
    } else {
      const nextIndex = currentIndex + 1;

      setValues((prev) =>
        prev.map((p, index) => ({
          ...p,
          isCurrent: index === nextIndex,
        }))
      );
    }
  };

  const handlePrevious = async (
    values: CustomQuestionnaireFormType[],
    setValues: FormikProps<CustomQuestionnaireFormType[]>["setValues"]
  ) => {
    const currentTemplate = values.find((value) => !!value.isCurrent);
    const templates = templateData?.templates || [];

    const currentIndex = templates.findIndex(
      (t) => t.id === currentTemplate?.templateId
    );
    const isFirstTemplate = currentIndex === 0;

    if (isFirstTemplate && prevSection) {
      navigateToSection(prevSection);
    } else {
      const previousIndex = currentIndex - 1;

      setValues((prev) =>
        prev.map((p, index) => ({
          ...p,
          isCurrent: index === previousIndex,
        }))
      );
    }
  };

  const isConditionalQuestion = (input: {
    name: string;
    values: CustomQuestionnaireFormType[];
  }): { isConditional: boolean; isConditionFulfilled: boolean } => {
    const { name, values } = input;

    const currentTemplate = values.find((value) => !!value.isCurrent);
    const currentTemplateData = templateData.templates.find(
      (c) => c.id === currentTemplate?.templateId
    );

    if (!currentTemplateData) {
      return { isConditional: false, isConditionFulfilled: true };
    }

    const conditions = currentTemplateData.template_input_claim_conditions;

    const condition = conditions.find(
      (condition) => condition.dependent_claim_key === name
    );

    if (!condition) {
      return { isConditional: false, isConditionFulfilled: true };
    }

    const answers = currentTemplate?.answers ?? {};

    return {
      isConditional: true,
      isConditionFulfilled: isConditionFulfilled({
        condition,
        answers,
      }),
    };
  };

  return (
    <Stack gap="space-6">
      <Formik<CustomQuestionnaireFormType[]>
        initialValues={initialValues}
        enableReinitialize
        validateOnMount
        validateOnChange
        // Build a dynamic schema that turns a question to required if the condition is fulfilled
        validationSchema={Yup.array().of(
          Yup.lazy((value: CustomQuestionnaireFormType) =>
            buildCustomQuestionnaireFormSchema(
              value.claims || [],
              value.conditions || [],
              value.answers ?? {},
              t
            )
          )
        )}
        onSubmit={(_, { setSubmitting }) => {
          setSubmitting(false);
        }}
      >
        {({ values, setValues, isValid, errors, handleBlur, touched }) => {
          const currentTemplate = values.find((value) => !!value.isCurrent);

          return (
            <Stack gap="space-8">
              <div className={styles.heading}>
                <Text as="h1" style="form/page-title" testID="template-title">
                  {currentTemplate?.name ||
                    sectionLabel ||
                    t("Custom Questionnaire")}
                </Text>

                <Text
                  style="form/body-primary"
                  color="text-weak"
                  testID="consent-sub-title"
                >
                  {subLabel}
                </Text>
              </div>

              <Stack gap="space-4">
                {currentTemplate?.claims.map((claim, index) => {
                  const { isConditional, isConditionFulfilled } =
                    isConditionalQuestion({
                      name: claim.name,
                      values,
                    });

                  if (isConditional && !isConditionFulfilled) return null;

                  const currentTemplateIndex = values.indexOf(currentTemplate);

                  const value = values.find(
                    (v) => v.templateId === currentTemplate.templateId
                  )?.answers[claim.name];
                  const error =
                    errors[currentTemplateIndex]?.answers?.[claim.name];
                  const isTouched =
                    touched[currentTemplateIndex]?.answers?.[claim.name];

                  return (
                    <Stack gap="space-0" key={claim.name}>
                      {!isEqual(index, 0) && (
                        <Divider marginVertical="space-4" />
                      )}
                      <CustomQuestionnaireField
                        {...claim}
                        name={`[${currentTemplateIndex}].answers.${claim.name}`}
                        value={isNil(value) ? undefined : value.toString()}
                        handleBlur={handleBlur}
                        errors={isTouched ? error : undefined}
                        onChange={(value) => {
                          updateValues({
                            currentTemplateId: currentTemplate.templateId,
                            claim: claim.name,
                            answer: value,
                            setValues,
                          });

                          updateContextValues({
                            claim: claim.name,
                            answer: value,
                            isValid,
                            // Save context based on the new values
                            values: values.map((v) =>
                              v.templateId === currentTemplate.templateId
                                ? {
                                    ...v,
                                    answers: {
                                      ...v.answers,
                                      [claim.name]: value,
                                    },
                                  }
                                : v
                            ),
                          });
                        }}
                      />
                    </Stack>
                  );
                })}

                <div className={styles.actionBar}>
                  <ButtonGroup>
                    {prevSection && (
                      <GhostButton
                        leftIcon="chevron_left"
                        onPress={() => {
                          handlePrevious(values, setValues);
                        }}
                        testID="section-back-button"
                      >
                        <Trans>Back</Trans>
                      </GhostButton>
                    )}
                    <Space />
                    <PrimaryButton
                      onPress={() => {
                        handleNext(values, setValues, isValid, errors);
                      }}
                      testID="section-next-step-button"
                    >
                      <Trans>Next step</Trans>
                    </PrimaryButton>
                  </ButtonGroup>
                </div>
              </Stack>
            </Stack>
          );
        }}
      </Formik>
    </Stack>
  );
}

const styles = {
  heading: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-2)",
  }),
  actionBar: css({
    paddingTop: `calc(${"var(--space-4)"} - 1px)`,
    paddingBottom: "var(--space-4)",
    marginTop: "var(--space-5)",
    borderTop: "1px solid var(--border-base)",
  }),
};
